import React, { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../common/types';
import { ErrorView } from '../../common/views/ErrorView/ErrorView';
import { LoadingView } from '../../common/views/LoadingView';
import { useGetDirectedVideoHistoryQuery } from '../directedVideoApi';
import { useDirectedVideoContext } from '../DirectedVideoContext';
import { howMadeMessages as messages } from '../messages/howMadeMessages';
import { DirectedStepLayout } from '../shell/DirectedStepLayout';
import { HowMadeCast } from './HowMadeCast';
import { HowMadeNav } from './HowMadeNav';
import { HowMadePrice } from './HowMadePrice';
import { HowMadeScript } from './HowMadeScript';
import { HowMadeStories } from './HowMadeStories';
import { HowMadeStyles } from './HowMadeStyles';
import { madeSectionId } from './howMadeSections';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for "How it was made"; each part uses its own slice of it */
import styles from './HowMade.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * "How it was made" (design directed-video l1, l2): every step of a video whose make started, read
 * only. Each step shows what the artist picked and the other options of the director, with every
 * round (`GET .../history`). A video from before the history shows the last set of each step. The
 * step of `?step=` scrolls into view.
 */
const HowItWasMadeComponent: React.FC = () => {
  const intl = useIntl();
  const { accountId, catalogItemUuid, video, madeStep } = useDirectedVideoContext();
  const args = useMemo(
    () => ({ accountId, catalogItemUuid, uuid: video.uuid }),
    [accountId, catalogItemUuid, video.uuid]
  );
  const history = useGetDirectedVideoHistoryQuery(args);
  const entries = useMemo(() => history.data?.entries ?? [], [history.data]);
  const isReady = history.data !== undefined;

  useEffect(() => {
    if (!isReady || madeStep === null) {
      return;
    }
    document.getElementById(madeSectionId(madeStep))?.scrollIntoView({ block: 'start' });
  }, [isReady, madeStep]);

  let body: React.ReactNode;
  if (history.isLoading) {
    body = <LoadingView label={intl.formatMessage(messages.loading)} />;
  } else if (history.error !== undefined && history.data === undefined) {
    body = (
      <ErrorView
        error={history.error}
        retry={() => {
          history.refetch().catch(console.error);
        }}
      />
    );
  } else {
    body = (
      <div className={styles.sections}>
        <HowMadeCast />
        <HowMadeStories entries={entries} />
        <HowMadeStyles entries={entries} />
        <HowMadeScript entries={entries} />
        <HowMadePrice entries={entries} />
      </div>
    );
  }

  return (
    <DirectedStepLayout>
      <div className={styles.page} data-testid="directed-made">
        <HowMadeNav />
        {body}
      </div>
    </DirectedStepLayout>
  );
};

export const HowItWasMade = TypedMemo(HowItWasMadeComponent);
